// AvantLink (AVM) affiliate order confirmation tracking.
// Pushes order + item data into the global _AvantMetrics queue,
// which the sitewide AVM script (loaded in index.html) reads.

declare global {
  interface Window {
    _AvantMetrics?: unknown[];
  }
}

export interface AvmOrderItem {
  parent_sku: string;
  variant_sku?: string;
  product_name?: string;
  price: number; // unit price, dollars, incl. discounts, excl. tax/shipping
  qty: number;
}

export interface AvmOrderData {
  order_id: string;
  amount: number; // subtotal excl. tax & shipping
  currency?: string;
  state?: string;
  country?: string;
  ecc?: string | null; // coupon code
  new_customer?: 'Y' | 'N';
  tax?: number;
  shipping?: number;
  items: AvmOrderItem[];
}

const fired = new Set<string>();

let avmScriptLoaded = false;

// Injects the sitewide AVM script on demand (used on the order confirmation
// page so the order data is queued before the script runs).
const loadAvmScript = () => {
  if (avmScriptLoaded) return;
  avmScriptLoaded = true;
  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = (window.location.protocol === 'https:' ? 'https://' : 'http://') + 'cdn.avmws.com/1042325/';
  document.body.appendChild(s);
};


// ---- AvantLink referral attribution ----
// When a visitor arrives via an AvantLink affiliate link, the URL carries
// ?avad=...&avad_ttl=<unix-seconds>. We persist that so the checkout/success
// pages (and the admin commissions view) know the order came via AvantLink.
const ATTR_KEY = 'avm_attribution';

export interface AvmAttribution {
  avad: string;
  expiresAt: number; // unix seconds, from avad_ttl (0 = unknown)
  capturedAt: number;
}

export const captureAvmAttribution = () => {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const avad = params.get('avad');
    if (!avad) return;
    const ttl = parseInt(params.get('avad_ttl') || '0', 10);
    const attribution: AvmAttribution = {
      avad,
      expiresAt: isNaN(ttl) ? 0 : ttl,
      capturedAt: Math.floor(Date.now() / 1000),
    };
    localStorage.setItem(ATTR_KEY, JSON.stringify(attribution));
  } catch {
    /* ignore */
  }
};

export const getAvmAttribution = (): AvmAttribution | null => {
  try {
    const raw = localStorage.getItem(ATTR_KEY);
    if (!raw) return null;
    const attr = JSON.parse(raw) as AvmAttribution;
    // Expired attribution no longer counts
    if (attr.expiresAt && attr.expiresAt < Math.floor(Date.now() / 1000)) {
      localStorage.removeItem(ATTR_KEY);
      return null;
    }
    return attr;
  } catch {
    return null;
  }
};

export const isAvmAttributed = (): boolean => getAvmAttribution() !== null;

export const trackAvmOrder = (order: AvmOrderData) => {
  try {
    if (typeof window === 'undefined') return;
    // Fire at most once per order id
    if (fired.has(order.order_id)) return;
    fired.add(order.order_id);

    window._AvantMetrics = window._AvantMetrics || [];

    window._AvantMetrics.push(['order', {
      order_id: order.order_id,
      amount: order.amount.toFixed(2),
      currency: order.currency || 'USD',
      state: order.state || '',
      country: order.country || 'USA',
      ecc: order.ecc || '',
      new_customer: order.new_customer || 'N',
      tax: (order.tax ?? 0).toFixed(2),
      shipping: (order.shipping ?? 0).toFixed(2),
    }]);

    for (const item of order.items) {
      window._AvantMetrics.push(['item', {
        order_id: order.order_id,
        parent_sku: item.parent_sku,
        variant_sku: item.variant_sku || item.parent_sku,
        product_name: item.product_name || '',
        price: item.price.toFixed(2),
        qty: String(item.qty),
      }]);
    }

    // Load the sitewide AVM script AFTER the order/item arrays are queued.
    // AvantLink requires the order data to exist prior to the sitewide script.
    loadAvmScript();

    console.log('[AVM] Order tracked:', order.order_id, order.amount.toFixed(2));

  } catch (e) {
    console.warn('[AVM] tracking skipped', e);
  }
};
